import type { WindowConfiguration, WindowMeasurements, WindowSketchModel } from '@termovista/window-configurator'
import type { ShutterConfiguration } from '../../../shared/shutters'
import type { MosquitoNetConfiguration } from '../../../shared/mosquitoNets'
import type { DoorConfiguration, DoorWriteInput } from '../../../shared/doors'
import type { FrameProductLine } from '../../../shared/frameProductLines'

export { FRAME_PRODUCT_LINES } from '../../../shared/frameProductLines'
export const HANDLE_PRODUCT_LINES = ['Roto Secustik'] as const
export const HANDLE_COLORS = ['Match Window'] as const
export const OPENING_PRODUCT_DEFAULTS = {
  frameProductLine: 'EURO DESIGN 70',
  handleProductLine: 'Roto Secustik',
  handleColor: 'Match Window',
} as const

export type { FrameProductLine } from '../../../shared/frameProductLines'
export type HandleProductLine = typeof HANDLE_PRODUCT_LINES[number]
export type HandleColor = string

export interface Opening {
  door?: DoorConfiguration
  shutter?: ShutterConfiguration
  mosquitoNet?: MosquitoNetConfiguration
  associatedOpeningId?: string | null
  id: string
  openingCode: string
  opportunityId: string
  siteVisitId: string | null
  label: string
  room: string | null
  elementType: string | null
  quantity: number
  measurements: WindowMeasurements
  configuration: WindowConfiguration
  configurationStatus: { valid: boolean; warning: string | null }
  glassConfiguration: { id: string; name: string | null; defaultBuildUp: string | null } | null
  customerSelections: {
    materialPreference: string | null
    colorInsideId: string | null
    colorOutsideId: string | null
    colorInside: string | null
    colorOutside: string | null
  }
  productSelections: {
    frameProductLine: FrameProductLine | null
    handleProductLine: HandleProductLine | null
    handleColor: HandleColor | null
    handleColorId?: string | null
  }
  requirements: {
    shutterRequired: boolean
    mosquitoNetRequired: boolean
    removalRequired: boolean
    installationComplexity: 'low' | 'medium' | 'high'
    measurementConfidence: string | null
    trimRequired: boolean
    trimType: 'Match Window' | 'Other' | null
    trimCustom: string | null
    trimWidthMm: '30' | '40' | '50' | '60' | null
  }
  notes: string | null
  internalNotes: string | null
  summary: { dimensions: string; operation: string }
  links: { quoteItemCount: number; supplierOrderItemCount: number; issueCount: number }
  createdAt: string | null
  updatedAt: string | null
}

export type OpeningPayload = {
  door?: DoorWriteInput
  shutter?: ShutterConfiguration
  mosquitoNet?: MosquitoNetConfiguration
  associatedOpeningId?: string | null
  siteVisitId: string | null
  label?: string
  room: string | null
  elementType: string
  quantity: number
  measurements: WindowMeasurements
  configuration: WindowConfiguration
  glassConfigurationId: string | null
  customerSelections: { colorInsideId: string | null; colorOutsideId: string | null }
  productSelections: {
    frameProductLine: FrameProductLine
    handleProductLine: HandleProductLine
    handleColor: HandleColor | null
    handleColorId?: string | null
  }
  requirements: {
    installationComplexity: 'low' | 'medium' | 'high'
    shutterRequired: boolean
    mosquitoNetRequired: boolean
    trimRequired: boolean
    trimType: 'Match Window' | 'Other' | null
    trimCustom: string | null
    trimWidthMm: '30' | '40' | '50' | '60' | null
  }
  notes: string | null
  internalNotes: string | null
}

export type GlassConfiguration = {
  id: string
  code: string
  name: string
  defaultBuildUp: string | null
  active: boolean
  isDefault: boolean
  configType: string | null
}

export type ColorOption = {
  productTypes?: string | string[] | null
  id: string
  codename: string
  finishCode: string | null
  name: string | null
  family: string | null
  texture: string | null
  isDefault: boolean
}

export const asSketchModel = (opening: Pick<Opening, 'measurements' | 'configuration'> & Partial<Opening>): WindowSketchModel & Pick<Opening, 'elementType' | 'shutter' | 'mosquitoNet'> => ({
  elementType: opening.elementType ?? null, shutter: opening.shutter, mosquitoNet: opening.mosquitoNet,
  measurements: opening.measurements,
  configuration: opening.configuration,
})
